import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Settings } from 'lucide-react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { Resource } from '../../../types';
import { ResourceTypeSelector } from './ResourceTypeSelector';
import { ResourceConfigurationPanel } from './ResourceConfigurationPanel';
import { getTranslatedResourceTypes } from './data';

interface ResourceSelectorProps {
  resources: Record<string, Resource>;
  onResourcesChange: (resources: Record<string, Resource>) => void;
}

export const ResourceSelector: React.FC<ResourceSelectorProps> = ({
  resources,
  onResourcesChange
}) => {
  const { t } = useTranslation();
  
  // Get translated resource types
  const resourceTypes = getTranslatedResourceTypes(t);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [showAddResource, setShowAddResource] = useState(false);

  const addResource = (resourceType: any) => {
    const newResourceId = `resource_${Date.now()}`;
    const newResource = {
      id: newResourceId,
      ...resourceType.defaultConfig
    };
    
    onResourcesChange({
      ...resources,
      [newResourceId]: newResource
    });
    
    setSelectedResourceId(newResourceId);
    setShowAddResource(false);
  };

  const updateResource = (resourceId: string, updates: Partial<Resource>) => {
    onResourcesChange({
      ...resources,
      [resourceId]: {
        ...resources[resourceId],
        ...updates
      }
    });
  };

  const removeResource = (resourceId: string) => {
    const newResources = { ...resources };
    delete newResources[resourceId];
    onResourcesChange(newResources);
    
    if (selectedResourceId === resourceId) {
      setSelectedResourceId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Resources Section */}
      <div className="space-y-4">
        {/* Add Resource Button */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">{t('resourceSelector.resources')}</h3>
          <button
            type="button" 
            onClick={() => setShowAddResource(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t('resourceSelector.addResource')}</span>
          </button>
        </div>

        {/* Resource List */}
        <div className="space-y-2">
          {Object.entries(resources).map(([resourceId, resource]) => (
            <div key={resourceId} className="space-y-2">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedResourceId === resourceId
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedResourceId(selectedResourceId === resourceId ? null : resourceId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Settings className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{resource.type}</h4>
                      <p className="text-sm text-gray-600">{t('resourceSelector.id')}: {resourceId}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedResourceId(selectedResourceId === resourceId ? null : resourceId);
                      }}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title={selectedResourceId === resourceId ? t('resourceSelector.hideConfiguration') : t('resourceSelector.configure')}
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeResource(resourceId);
                      }}
                      className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Inline Configuration Panel */}
              {selectedResourceId === resourceId && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                >
                  <ResourceConfigurationPanel
                    resource={resource}
                    resourceId={resourceId}
                    onUpdate={updateResource}
                    onClose={() => setSelectedResourceId(null)}
                    inline={true}
                  />
                </motion.div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Add Resource Modal */}
      {showAddResource && (
        <ResourceTypeSelector
          resourceTypes={resourceTypes}
          onSelect={addResource}
          onClose={() => setShowAddResource(false)}
        />
      )}
    </div>
  );
};
